import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { CatmullRomCurve3 } from 'three';
import { Instance, Instances } from '@react-three/drei';
import * as THREE from 'three';

// --- SHARED GEOMETRIES ---
const GEOMETRY = {
    nose: new THREE.ConeGeometry(0.5, 1.2, 16),
    hull: new THREE.BoxGeometry(1.0, 0.4, 2.0),
    sideWall: new THREE.BoxGeometry(0.1, 0.6, 1.8),
    backWall: new THREE.BoxGeometry(1.0, 0.6, 0.1),
    seat: new THREE.BoxGeometry(0.8, 0.4, 0.4),
    head: new THREE.SphereGeometry(0.12, 8, 8),
    body: new THREE.CylinderGeometry(0.15, 0.15, 0.4, 8),
    windshield: new THREE.PlaneGeometry(0.9, 0.4),
    skid: new THREE.BoxGeometry(0.2, 0.2, 0.6),
    bulb: new THREE.SphereGeometry(0.15, 16, 8),
};

// Reusable TrainCar with Shared Geometry
const TrainCar = React.forwardRef(({ isHead = false }, ref) => {
    const spotLightRef = useRef();
    const targetRef = useRef();

    useFrame(() => {
        if (isHead && spotLightRef.current && targetRef.current) {
            spotLightRef.current.target = targetRef.current;
        }
    });

    return (
        <group ref={ref}>
            {/* 1. Aerodynamic Nose */}
            <mesh geometry={GEOMETRY.nose} position={[0, 0.4, 1.6]} rotation={[Math.PI / 2, 0, 0]}>
                <meshStandardMaterial color="#e0e0e0" metalness={0.9} roughness={0.1} />
            </mesh>

            {/* 2. Main Hull */}
            <mesh geometry={GEOMETRY.hull} position={[0, 0.2, 0]}>
                <meshStandardMaterial color="#e0e0e0" metalness={0.9} roughness={0.1} />
            </mesh>

            {/* 3. Side Walls */}
            <mesh geometry={GEOMETRY.sideWall} position={[0.45, 0.6, 0]}>
                <meshStandardMaterial color="#00bcd4" metalness={0.8} roughness={0.2} />
            </mesh>
            <mesh geometry={GEOMETRY.sideWall} position={[-0.45, 0.6, 0]}>
                <meshStandardMaterial color="#00bcd4" metalness={0.8} roughness={0.2} />
            </mesh>
            <mesh geometry={GEOMETRY.backWall} position={[0, 0.6, -0.95]}>
                <meshStandardMaterial color="#00bcd4" metalness={0.8} roughness={0.2} />
            </mesh>

            {/* 4. Interior Seats */}
            <mesh geometry={GEOMETRY.seat} position={[0, 0.4, 0.4]}>
                <meshStandardMaterial color="#333" roughness={0.9} />
            </mesh>
            <mesh geometry={GEOMETRY.seat} position={[0, 0.4, -0.4]}>
                <meshStandardMaterial color="#333" roughness={0.9} />
            </mesh>

            {/* 5. PASSENGERS */}
            {/* Front Row Left */}
            <group position={[-0.25, 0.6, 0.4]}>
                <mesh geometry={GEOMETRY.head} position={[0, 0.3, 0]}>
                    <meshStandardMaterial color="#f0c0a0" />
                </mesh>
                <mesh geometry={GEOMETRY.body} position={[0, 0, 0]}>
                    <meshStandardMaterial color="red" />
                </mesh>
            </group>
            {/* Front Row Right */}
            <group position={[0.25, 0.6, 0.4]}>
                <mesh geometry={GEOMETRY.head} position={[0, 0.3, 0]}>
                    <meshStandardMaterial color="#553311" />
                </mesh>
                <mesh geometry={GEOMETRY.body} position={[0, 0, 0]}>
                    <meshStandardMaterial color="blue" />
                </mesh>
            </group>

            {/* Back Row Left */}
            <group position={[-0.25, 0.6, -0.4]}>
                <mesh geometry={GEOMETRY.head} position={[0, 0.3, 0]}>
                    <meshStandardMaterial color="#ffdab9" />
                </mesh>
                <mesh geometry={GEOMETRY.body} position={[0, 0, 0]}>
                    <meshStandardMaterial color="green" />
                </mesh>
            </group>
            {/* Back Row Right */}
            <group position={[0.25, 0.6, -0.4]}>
                <mesh geometry={GEOMETRY.head} position={[0, 0.3, 0]}>
                    <meshStandardMaterial color="#8d5524" />
                </mesh>
                <mesh geometry={GEOMETRY.body} position={[0, 0, 0]}>
                    <meshStandardMaterial color="yellow" />
                </mesh>
            </group>

            {/* 6. Windshield */}
            <mesh geometry={GEOMETRY.windshield} position={[0, 0.7, 0.8]} rotation={[-Math.PI / 4, 0, 0]}>
                <meshPhysicalMaterial
                    color="#88ccff"
                    metalness={0}
                    roughness={0}
                    transmission={0.9}
                    transparent
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* 7. Wheels (Skids) */}
            <mesh geometry={GEOMETRY.skid} position={[0.4, 0.1, 0.6]}><meshStandardMaterial color="#111" /></mesh>
            <mesh geometry={GEOMETRY.skid} position={[-0.4, 0.1, 0.6]}><meshStandardMaterial color="#111" /></mesh>
            <mesh geometry={GEOMETRY.skid} position={[0.4, 0.1, -0.6]}><meshStandardMaterial color="#111" /></mesh>
            <mesh geometry={GEOMETRY.skid} position={[-0.4, 0.1, -0.6]}><meshStandardMaterial color="#111" /></mesh>

            {/* 8. HEADLIGHTS - ONLY IF HEAD CAR */}
            {isHead && (
                <>
                    <mesh geometry={GEOMETRY.bulb} position={[0, 0.4, 1.6]}>
                        <meshBasicMaterial color="#ffffff" toneMapped={false} />
                    </mesh>

                    <object3D ref={targetRef} position={[0, 0, 20]} />
                    <spotLight
                        ref={spotLightRef}
                        position={[0, 1, 1.5]}
                        color="#ffffff"
                        intensity={50}
                        angle={0.5}
                        penumbra={0.2}
                        distance={40}
                        castShadow
                    />
                </>
            )}
        </group>
    );
});

const NEON_PALETTE = [
    new THREE.Color('#ff00ff'),
    new THREE.Color('#00ffff'),
    new THREE.Color('#39ff14'),
    new THREE.Color('#ffff00'),
    new THREE.Color('#ff0055'),
];
const COLOR_DIM = new THREE.Color('#000000');

const TrackLights = ({ curve }) => {
    const count = 400;
    const points = useMemo(() => curve.getSpacedPoints(count), [curve]);
    const instancesRef = useRef();

    useFrame((state) => {
        if (instancesRef.current) {
            const time = state.clock.getElapsedTime();
            for (let i = 0; i < count; i++) {
                const shift = i + (time * 8);

                // 1. Selector for which color to use
                const colorIndex = Math.floor(Math.abs(shift * 0.2)) % NEON_PALETTE.length;

                // 2. Blinking Logic
                const blink = Math.sin(shift * 0.8);

                const color = blink > 0.8 ? NEON_PALETTE[colorIndex] : COLOR_DIM;

                instancesRef.current.setColorAt(i, color);
            }
            instancesRef.current.instanceColor.needsUpdate = true;
        }
    });

    return (
        <Instances ref={instancesRef} range={count}>
            <sphereGeometry args={[0.17, 9, 9]} />
            <meshBasicMaterial toneMapped={false} />
            {points.map((pos, i) => (
                <Instance key={i} position={pos} />
            ))}
        </Instances>
    );
};


const RollerCoaster = () => {
    const curve = useMemo(() => {
        return new CatmullRomCurve3([
            // Visible Path (The "Corkscrew" Stunt)

            // 1. Entry from Left (Offscreen)
            new THREE.Vector3(-30, -5, -10),
            new THREE.Vector3(-25, -5, -10),
            new THREE.Vector3(-20, -5, -10),
            new THREE.Vector3(-15, -5, -10),

            // 2. Climb up to the stunt
            new THREE.Vector3(-15, 5, -12),
            new THREE.Vector3(-20, 7, -19),
            new THREE.Vector3(-25, 10, -26),
            new THREE.Vector3(-15, 15, -33),
            new THREE.Vector3(-10, 15, -33),

            // 3. The CORKSCREW STUNT (Spiraling through center)
            new THREE.Vector3(-5, 0, -8),
            new THREE.Vector3(0, -5, -12),
            new THREE.Vector3(5, 0, -16),
            new THREE.Vector3(10, 5, -20),

            // 4. Recovery / Camel Hump
            new THREE.Vector3(15, 8, -12),
            new THREE.Vector3(20, 5, -19),
            new THREE.Vector3(25, 10, -26),
            new THREE.Vector3(15, 15, -33),

            // 5. Dive to Exit
            new THREE.Vector3(25, -5, -10),
            new THREE.Vector3(30, -3, -10),
            new THREE.Vector3(35, 5, -10),
            new THREE.Vector3(40, 5, -10),
            new THREE.Vector3(45, 0, -12),
            new THREE.Vector3(50, 0, 10),

            // Hidden Return Path
            new THREE.Vector3(70, 10, 10),
            new THREE.Vector3(65, 30, 10),
            new THREE.Vector3(60, 35, 10),
            new THREE.Vector3(55, 20, 10),
            new THREE.Vector3(50, 35, 10),
            new THREE.Vector3(45, 35, -5),
            new THREE.Vector3(40, -35, -5),
            new THREE.Vector3(-25, -35, -5),
            new THREE.Vector3(-30, -5, -10)
        ], true);
    }, []);

    const trainRefs = useRef([]);

    useFrame((state) => {
        const time = state.clock.getElapsedTime();
        const baseSpeed = 0.05;
        const carSpacing = 0.005; // Standard spacing for bullet train

        trainRefs.current.forEach((ref, index) => {
            if (ref) {
                // Calculate position for each car
                // Head car is at index 0, following cars are behind (minus offset)
                let t = (time * baseSpeed - (index * carSpacing)) % 1;
                if (t < 0) t += 1; // Handle wrap-around

                const position = curve.getPointAt(t);

                // Look ahead for orientation
                let lookAtT = (t + 0.01) % 1;
                if (lookAtT < t) lookAtT += 1;

                const lookAtPos = curve.getPointAt(lookAtT % 1);

                ref.position.copy(position);
                ref.lookAt(lookAtPos);
            }
        });
    });

    // Create array of 4 cars
    const cars = Array.from({ length: 4 });

    return (
        <group>
            {/* The Track (Glossy Dark Metal) */}
            <mesh>
                <tubeGeometry args={[curve, 300, 0.15, 7, true]} />
                <meshStandardMaterial
                    color="#000"
                    roughness={0.1}
                    metalness={1}
                />
            </mesh>

            {/* TRACK LIGHTS (Dense Amber Bulbs) */}
            <TrackLights curve={curve} />

            {/* The Bullet Train (4 Connected Cars) */}
            {cars.map((_, i) => (
                <TrainCar
                    key={i}
                    ref={(el) => (trainRefs.current[i] = el)}
                    isHead={i === 0}
                />
            ))}
        </group>
    );
};

export default RollerCoaster;
